import React, { useEffect, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { Tabs } from "antd";
import { CMS_maati } from "urlconfig";

const CropLevelData = ({ farmerId, selectedCrop }) => {
  const cropId = selectedCrop?.id;

  const [activeTab, setActiveTab] = useState("farmer_crop_level_1");
  const [loading, setLoading] = useState(false);
  const [cropDetail, setCropDetail] = useState(null);
  const [tabCache, setTabCache] = useState({});
  const [labels, setLabels] = useState([]);
  const [stages, setStages] = useState([]);
  const [groups, setGroups] = useState([]);

  const formatKey = (key) =>
    key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  const getApiUrl = () => {
    if (activeTab === "farmer_crop_level_1") {
      return `${CMS_maati}/api/farmer-crop-level-1s?filters[farmerId][$eq]=${farmerId}`;
    } else if (activeTab === "farmer_crop_level_2") {
      return `${CMS_maati}/api/farmer-crop-level-2s?filters[crop_id][$eq]=${cropId}`;
    } else if (activeTab === "farmer_crop_level_3") {
      return `${CMS_maati}/api/farmer-crop-level-3s?filters[crop_id][$eq]=${cropId}`;
    }
    return null;
  };

  useEffect(() => {
    async function fetchLabels() {
      try {
        const res = await fetch(
          `${CMS_maati}/api/ui-schemas?filters[form_name]=${activeTab}&filters[language]=en`
        );
        const data = await res.json();

        setLabels(data?.data[0]?.labels?.form_data?.components || []);
        setGroups(data?.data[0]?.labels?.groups || []);

        if (activeTab === "farmer_crop_level_2") {
          setStages(data?.data[0]?.labels?.groups || []);
        }
      } catch (err) {
        console.error("API fetch error:", err);
      }
    }

    fetchLabels();
  }, [activeTab]);

  useEffect(() => {
    if (!cropId) return;

    if (tabCache[activeTab]) {
      setCropDetail(tabCache[activeTab]);
      return;
    }

    const fetchData = async () => {
      setLoading(true);

      try {
        const apiUrl = getApiUrl();
        const res = await fetch(apiUrl);
        const json = await res.json();

        let data = null;

        if (Array.isArray(json?.data)) {
          if (activeTab === "farmer_crop_level_1") {
            data = json.data.find((item) => item.documentId === cropId) || null;
          } else {
            data = json.data[0] || null;
          }
        } else {
          data = json?.data || null;
        }

        setCropDetail(data || null);
        setTabCache((prev) => ({ ...prev, [activeTab]: data || null }));
      } catch (err) {
        console.error("Error fetching crop data:", err);
        setCropDetail(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [activeTab, cropId]);

  const getActiveCropData = () => {
    if (!cropDetail) return null;

    if (activeTab === "farmer_crop_level_1") {
      const level1Data = cropDetail?.farmer_crop_level_1;
      if (!level1Data) return null;

      return Object.fromEntries(
        Object.entries(level1Data).filter(([k]) => k !== "crop_group_list_id")
      );
    }

    if (activeTab === "farmer_crop_level_2")
      return cropDetail?.farmer_crop_level_2;

    if (activeTab === "farmer_crop_level_3")
      return cropDetail?.farmer_crop_level_3;

    return null;
  };

  const activeData = getActiveCropData();

  const getLabelForKey = (key) => {
    const found = labels.find((item) => item.key === key);
    return found?.label || formatKey(key);
  };

  const getGroupForKey = (key) => {
    const found = labels.find((item) => item.key === key);
    return found?.group || "General Information";
  };

  return (
    <div style={{ marginTop: "-8px" }}>
      <Tabs
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key)}
        items={[
          { key: "farmer_crop_level_1", label: "Crop Level 1" },
          { key: "farmer_crop_level_2", label: "Crop Level 2" },
          { key: "farmer_crop_level_3", label: "Crop Level 3" },
        ]}
      />

      {loading && !activeData ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : activeData ? (
        <>
          {activeTab === "farmer_crop_level_2" ? (
            stages.map((stage) => (
              <div key={stage} className="mb-4">
                <h5 className="fw-bold text-primary mb-3">{stage}</h5>

                <div className="row g-3">
                  {Object.entries(activeData)
                    .filter(([key]) => getGroupForKey(key) === stage)
                    .map(([key, value]) => {
                      if (
                        [
                          "id",
                          "documentId",
                          "createdAt",
                          "updatedAt",
                          "publishedAt",
                          "farmer_id",
                        ].includes(key)
                      )
                        return null;

                      let displayValue = "";
                      if (Array.isArray(value)) displayValue = value.join(", ");
                      else if (typeof value === "object" && value !== null)
                        displayValue = JSON.stringify(value);
                      else displayValue = value?.toString() || "-";

                      return (
                        <div className="col-md-6 col-lg-4" key={key}>
                          <div className="border rounded-3 shadow-sm p-3 bg-white overflow-scroll">
                            <div className="d-flex align-items-start">
                              <i className="mdi mdi-chevron-double-right text-success me-2 fs-5"></i>
                              <span className="fw-semibold text-dark mt-1 ">
                                {getLabelForKey(key)}
                              </span>
                            </div>
                            <div className="d-flex align-items-center">
                              <i className="mdi mdi-chevron-right me-2 fs-5"></i>
                              <span className="text-gray">{displayValue}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            ))
          ) : (
            // <div className="row g-3">
            //   {Object.entries(activeData).map(([key, value]) => {
            //     if (
            //       [
            //         "id",
            //         "documentId",
            //         "createdAt",
            //         "updatedAt",
            //         "publishedAt",
            //         "farmer_id",
            //       ].includes(key)
            //     )
            //       return null;

            //     let displayValue = "";
            //     if (Array.isArray(value)) displayValue = value.join(", ");
            //     else if (typeof value === "object" && value !== null)
            //       displayValue = JSON.stringify(value);
            //     else displayValue = value?.toString() || "-";

            //     return (
            //       <div className="col-md-6 col-lg-4" key={key}>
            //         <div className="border rounded-3 shadow-sm p-3 bg-white overflow-scroll">
            //           <div className="d-flex align-items-start">
            //             <i className="mdi mdi-chevron-double-right text-success me-2 fs-5"></i>
            //             <span className="fw-semibold text-dark mt-1">
            //               {getLabelForKey(key)}
            //             </span>
            //           </div>
            //           <div className="d-flex align-items-center">
            //             <i className="mdi mdi-chevron-right me-2 fs-5"></i>
            //             <span className="text-gray">{displayValue}</span>
            //           </div>
            //         </div>
            //       </div>
            //     );
            //   })}
            // </div>
            <>
              {groups.map((grp) => {
                const { name, sort_keys } = grp;

                const matchedEntries = Object.entries(
                  activeData
                ).filter(([key]) => sort_keys?.includes(key));

                if (matchedEntries.length === 0) return null;

                return (
                  <div key={name} className="mb-4">
                    <h5 className="fw-bold text-primary mb-3">{name}</h5>

                    <div className="row g-3">
                      {matchedEntries.map(([key, value]) => {
                        if (
                          [
                            "id",
                            "documentId",
                            "createdAt",
                            "updatedAt",
                            "publishedAt",
                            "farmer_id",
                          ].includes(key)
                        )
                          return null;

                        let displayValue = "";
                        if (Array.isArray(value))
                          displayValue = value.join(", ");
                        else if (typeof value === "object" && value !== null)
                          displayValue = JSON.stringify(value);
                        else displayValue = value?.toString() || "-";

                        return (
                          <div className="col-md-6 col-lg-4" key={key}>
                            <div className="border rounded-3 shadow-sm p-3 bg-white overflow-scroll">
                              <div className="d-flex align-items-start">
                                <i className="mdi mdi-chevron-double-right text-success me-2 fs-5"></i>
                                <span className="fw-semibold text-dark mt-1">
                                  {getLabelForKey(key)}
                                </span>
                              </div>
                              <div className="d-flex align-items-center">
                                <i className="mdi mdi-chevron-right me-2 fs-5"></i>
                                <span className="text-gray">
                                  {displayValue}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </>
      ) : (
        <Typography className="py-5 d-flex justify-content-center align-items-center">
          🚫 No details found
        </Typography>
      )}
    </div>
  );
};

export default CropLevelData;
